import { useCallback, useEffect, useRef, useState } from "react";
import { addDays, addMonths, format, isSameDay } from "date-fns";
import { useParams } from "react-router-dom";

import { env } from "~/env";
import {
  getDefaultTimeZone,
  useStatesListQuery,
  VISIT_TYPES,
  VisitType,
} from "~/services";
import { CalendarDay, TimeSlot, TimeZone } from "~/shared.types";
import { formatDateTimeForAPI } from "~/shared/utils";
import { AppointmentDetails, useSchedulingTimezoneStore } from "~/stores";
import {
  generateCalendarDaysForMonth,
  getFirstDayWithSlots,
  handleMonthChange,
  isSelectableDay,
} from "~/utils";
import {
  formatAppointmentTime,
  TIMEZONES,
  UseScheduleVisitReturn,
} from "../utils";
import {
  useGetFirstAvailableSlots,
  useGetProvidersSlots,
  useIpInfo,
  useUpdateURL,
} from "./index";
import { useTrackSlotDateViews } from "./useTrackSlotDateViews";

type UseScheduleVisitParams = {
  visitType: VisitType;
  stateId: number | null | undefined;
  providerId?: number | null;
  insuranceId?: number | null;
  isProviderView?: boolean;
  onContinue: (details: AppointmentDetails) => void;
};

export const useScheduleVisit = ({
  visitType,
  stateId,
  providerId,
  insuranceId,
  isProviderView = true,
  onContinue,
}: UseScheduleVisitParams): UseScheduleVisitReturn => {
  const { startDate } = useParams();
  const { updateURL } = useUpdateURL(startDate);

  const today = new Date();
  const maxAllowedDate = new Date(
    today.getFullYear(),
    today.getMonth() + Number(env.VITE_APPOINTMENT_SLOT_MAX_MONTHS),
    1,
  );

  const getDefaultStartDate = () => format(new Date(), "MM/dd/yyyy");

  const [currentMonthYear, setCurrentMonthYear] = useState(() => ({
    month: today.toLocaleDateString("en-US", { month: "long" }),
    year: today.getFullYear(),
  }));

  const [currentStartDate, setCurrentStartDate] = useState(
    startDate ?? getDefaultStartDate(),
  );

  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>(() =>
    generateCalendarDaysForMonth(today.getFullYear(), today.getMonth()),
  );

  const isFollowUpVisit = visitType === VISIT_TYPES.FOLLOW_UP;
  const shouldUseProvider = isFollowUpVisit && providerId;

  const { data: appointmentSlotsByProvider, isPending: isPendingProviderView } =
    useGetProvidersSlots({
      params: {
        stateId: stateId,
        appointmentType: visitType,
        startDate: currentStartDate,
        dateRange: "current_month",
        providerId: shouldUseProvider ? providerId : undefined,
        insuranceId: insuranceId,
      },
      enabled: isProviderView,
    });

  const {
    data: appointmentSlotsByFirstAvailable,
    isPending: isPendingFirstAvailableView,
  } = useGetFirstAvailableSlots({
    params: {
      stateId: stateId,
      appointmentType: visitType,
      startDate: currentStartDate,
      dateRange: "current_month",
      insuranceId: insuranceId,
    },
    enabled: !isProviderView,
  });

  const [selectedDay, setSelectedDay] = useState<CalendarDay>(() => {
    const days = generateCalendarDaysForMonth(
      today.getFullYear(),
      today.getMonth(),
    );

    const todayDay = days.find((day) => day.isToday && day.isAvailable);
    if (todayDay) return todayDay;

    const tomorrow = addDays(today, 1);
    const tomorrowDay = days.find(
      (day) => isSameDay(day.date, tomorrow) && day.isAvailable,
    );
    if (tomorrowDay) return tomorrowDay;

    return (
      getFirstDayWithSlots({
        days,
        appointmentSlotsByProvider,
        appointmentSlotsByFirstAvailable,
        isProviderView: isProviderView,
      }) ||
      days.find((day) => day.isAvailable) ||
      days[0]
    );
  });

  const { selectedTimeZone, setSelectedTimeZone } =
    useSchedulingTimezoneStore();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null,
  );
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [firstAvailableDay, setFirstAvailableDay] =
    useState<CalendarDay | null>(null);

  const { data: ipInfo, isPending: isPendingIpInfo } = useIpInfo();

  const { data: states, isPending: isPendingStates } = useStatesListQuery();
  const stateTimezone = states?.find((state) => state.id === stateId)?.timezone;

  const userWentBackRef = useRef(false);
  const hasResolvedDefaultTimeZone = useRef(false);

  useTrackSlotDateViews({
    date: selectedDay.date,
    providers: appointmentSlotsByProvider ?? [],
    enabled: isProviderView && !isPendingProviderView,
  });

  const hasAppointments = useCallback(
    (date: Date): boolean => {
      return isSelectableDay({
        date,
        appointmentSlotsByProvider,
        appointmentSlotsByFirstAvailable,
        isProviderView: isProviderView,
        providerId: shouldUseProvider ? providerId : undefined,
      });
    },
    [
      appointmentSlotsByProvider,
      appointmentSlotsByFirstAvailable,
      shouldUseProvider,
      providerId,
      isProviderView,
    ],
  );

  useEffect(() => {
    if (
      (isProviderView && isPendingProviderView) ||
      (!isProviderView && isPendingFirstAvailableView)
    )
      return;
    const firstDayWithAvailableSlots = getFirstDayWithSlots({
      days: calendarDays,
      appointmentSlotsByFirstAvailable,
      isProviderView,
      appointmentSlotsByProvider,
    });
    if (firstDayWithAvailableSlots) {
      setFirstAvailableDay((prev) => prev ?? firstDayWithAvailableSlots);
      setSelectedDay(firstDayWithAvailableSlots);
    } else {
      if (userWentBackRef.current) {
        userWentBackRef.current = false;
        return;
      }
      const nextMonth = addMonths(calendarDays[0].date, 1);
      if (nextMonth > maxAllowedDate) return;
      handleMonthChange({
        monthYear: {
          month: nextMonth.toLocaleString("en-US", { month: "long" }),
          year: nextMonth.getFullYear(),
        },
        hasAppointments,
        setCurrentMonthYear,
        setCurrentStartDate,
        setCalendarDays,
        setSelectedDay,
      });
    }
  }, [
    appointmentSlotsByProvider,
    appointmentSlotsByFirstAvailable,
    isProviderView,
    isPendingProviderView,
    isPendingFirstAvailableView,
  ]);

  useEffect(() => {
    // Resolves once, after both lookups settle. Running per lookup would let a late
    // states response overwrite a timezone the patient picked by hand in between, and
    // returning early while the IP is still pending is what lets an empty IP result
    // reach the state fallback at all.
    if (hasResolvedDefaultTimeZone.current) return;
    if (isPendingIpInfo || isPendingStates) return;

    hasResolvedDefaultTimeZone.current = true;

    const defaultTimeZoneCode = getDefaultTimeZone(
      ipInfo ?? null,
      stateTimezone,
    );
    setSelectedTimeZone(
      TIMEZONES[defaultTimeZoneCode as keyof typeof TIMEZONES] ?? TIMEZONES.ET,
    );
  }, [
    ipInfo,
    isPendingIpInfo,
    isPendingStates,
    stateTimezone,
    setSelectedTimeZone,
  ]);

  useEffect(() => {
    if (!startDate) updateURL();
    else setCurrentStartDate(startDate);
  }, [startDate, updateURL]);

  const handleMonthChangeCallback = async (monthYear: {
    month: string;
    year: number;
  }) => {
    const newDate = new Date(`${monthYear.month} 1, ${monthYear.year}`);
    if (newDate > maxAllowedDate) return;

    const currentMonthDate = new Date(
      `${currentMonthYear.month} 1, ${currentMonthYear.year}`,
    );
    userWentBackRef.current = newDate < currentMonthDate;

    await handleMonthChange({
      monthYear,
      hasAppointments,
      setCurrentMonthYear,
      setCurrentStartDate,
      setCalendarDays,
      setSelectedDay,
    });
  };

  const onDaySelect = (day: CalendarDay) => {
    setSelectedDay(day);
    setSelectedTimeSlot(null);
    setSelectedProvider(null);
    setIsCalendarOpen(false);
  };

  const onTimeZoneChange = (timeZone: TimeZone) => {
    setSelectedTimeSlot(null);
    setSelectedProvider(null);
    setSelectedTimeZone(timeZone);
  };

  const onTimeSlotSelect = (timeSlot: TimeSlot, providerId?: number) => {
    setSelectedTimeSlot(timeSlot);
    setSelectedProvider(providerId ?? null);
  };

  const onOpenCalendar = () => setIsCalendarOpen(true);
  const onCloseCalendar = () => setIsCalendarOpen(false);

  const handleReturnToFirstAvailable = useCallback(async () => {
    if (!firstAvailableDay) return;
    await handleMonthChangeCallback({
      month: firstAvailableDay.date.toLocaleString("en-US", { month: "long" }),
      year: firstAvailableDay.date.getFullYear(),
    });
  }, [firstAvailableDay, handleMonthChangeCallback]);

  const handleContinue = useCallback(() => {
    if (!selectedTimeSlot || !selectedProvider || !stateId) return;

    let providerSelected = appointmentSlotsByProvider?.find(
      (p) => p.id === selectedProvider,
    );
    if (!providerSelected) {
      providerSelected = {
        id: selectedProvider,
        name: selectedTimeSlot.providerName ?? "",
        title: selectedTimeSlot.providerTitle ?? "",
        slots: {},
        firstName: undefined,
        lastName: undefined,
        carePhilosophy: undefined,
        additionalExpertise: [],
        languages: [],
        testimonials: [],
        yearsOfExperience: undefined,
      };
    }

    const appointmentDate = format(selectedDay.date, "MM/dd/yyyy");
    const formattedDate = format(selectedDay.date, "EEEE, MMMM d");

    const timezoneAbbr = selectedTimeZone.code;

    const formattedTime = formatAppointmentTime(
      selectedTimeSlot.startTime,
      selectedTimeSlot.duration,
      selectedTimeZone,
    );

    const visitDateTime = formatDateTimeForAPI(
      appointmentDate,
      selectedTimeSlot.originalTime ?? "",
    );

    const appointmentDetails: AppointmentDetails = {
      provider: providerSelected,
      timeSlot: selectedTimeSlot,
      timezone: timezoneAbbr,
      date: formattedDate,
      formattedDate: appointmentDate,
      formattedTime: formattedTime,
      visitDateTime,
    } as AppointmentDetails;

    onContinue(appointmentDetails);
  }, [
    selectedTimeSlot,
    selectedProvider,
    stateId,
    appointmentSlotsByProvider,
    selectedDay.date,
    selectedTimeZone.name,
    onContinue,
  ]);

  return {
    calendar: {
      selectedDay,
      calendarDays,
      monthYear: currentMonthYear,
      maxAllowedDate,
      isCalendarOpen,
      hasAppointments,
      firstAvailableDay,
    },
    selection: {
      selectedTimeSlot,
      selectedProvider,
      selectedTimeZone,
    },
    calendarHandlers: {
      onDaySelect,
      onMonthChange: handleMonthChangeCallback,
      onOpenCalendar,
      onCloseCalendar,
      onReturnToFirstAvailable: handleReturnToFirstAvailable,
    },
    selectionHandlers: {
      onTimeZoneChange,
      onTimeSlotSelect,
    },
    isPendingProviderView,
    isPendingFirstAvailableView,
    appointmentSlotsByProvider: appointmentSlotsByProvider ?? [],
    appointmentSlotsByFirstAvailable: appointmentSlotsByFirstAvailable ?? {},
    handleContinue,
  };
};
